import { useEffect, useState } from 'react';
import { api } from './api.js';
import { PublishAnnouncement } from './Announcements.js';

interface Check { ok: boolean; detail: string }
interface Deep {
  ok: boolean; env: string; draining: boolean;
  uptimeSeconds: number; memoryMb: number;
  pool: Record<string, number>;
  checks: Record<string, Check>;
}
interface Job {
  job: string; runKey: string | null; ok: boolean | null;
  startedAt: string | null; durationMs: number | null; detail: string | null;
  expectedWithinHours: number;
}
interface Activity {
  at: string; action: string; entity: string; entity_id: string | null;
  method: string | null; path: string | null; status: number | null;
  duration_ms: number | null; request_id: string | null;
}

const LABEL: Record<string, string> = {
  database: 'Database',
  migrations: 'Migrations',
  rowLevelSecurity: 'Data isolation',
  backup: 'Backups',
  restoreDrill: 'Restore drill',
  scheduledJobs: 'Scheduled work',
  uploads: 'File storage',
  ai: 'AI',
};

const JOB_LABEL: Record<string, string> = {
  materialise_doses: 'Build upcoming doses',
  risk_snapshot: 'Record risk score',
  blood_due_check: 'Check bloods due',
  daily_brief: 'Daily brief',
  weekly_review: 'Weekly review',
};

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min ago`;
  if (h < 48) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * The page that answers "is this thing actually looking after my data".
 *
 * Backups and the restore drill are on it deliberately. They are the two things
 * that fail silently for months and then matter all at once, and putting them
 * next to the database check is the cheapest way to stop that happening.
 */
export function System(): JSX.Element {
  const [deep, setDeep] = useState<Deep | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // The deep check answers 503 when something is wrong, which is the point of
    // it, so a non-2xx body still has to be read rather than thrown away.
    void fetch('/api/health/deep')
      .then((r) => r.json() as Promise<Deep>)
      .then(setDeep)
      .catch((e: Error) => setErr(e.message));
    void api<Job[]>('/account/jobs').then(setJobs).catch(() => undefined);
    void api<Activity[]>('/account/activity?limit=60').then(setActivity).catch(() => undefined);
  }, []);

  if (err) return <div className="card"><p className="bad">{err}</p></div>;
  if (!deep) return <div className="card"><p>Checking…</p></div>;

  const uptime = deep.uptimeSeconds > 86_400
    ? `${Math.floor(deep.uptimeSeconds / 86_400)}d ${Math.floor((deep.uptimeSeconds % 86_400) / 3600)}h`
    : `${Math.floor(deep.uptimeSeconds / 3600)}h ${Math.floor((deep.uptimeSeconds % 3600) / 60)}m`;

  return (
    <>
      <div className="card">
        <h2>System</h2>
        <p className="sub">{deep.env} · up {uptime} · {deep.memoryMb} MB</p>
        <ul className="checklist">
          {Object.entries(deep.checks).map(([key, c]) => (
            <li key={key} className={c.ok ? 'ok' : 'bad'}>
              <strong>{LABEL[key] ?? key}</strong>
              <span>{c.detail}</span>
            </li>
          ))}
        </ul>
        {!deep.ok && (
          <p className="note">
            Something above is not ok. Backups and the restore drill will stay red until a
            backup destination and an age key are configured on the box.
          </p>
        )}
      </div>

      <PublishAnnouncement />

      {jobs.length > 0 && (
        <div className="card">
          <h2>Scheduled work</h2>
          <p className="sub">
            What the app does on its own, without anybody opening it.
          </p>
          <ul className="checklist">
            {jobs.map((j) => {
              const stale = j.startedAt
                ? (Date.now() - new Date(j.startedAt).getTime()) / 3_600_000 > j.expectedWithinHours
                : true;
              return (
                <li key={j.job} className={j.ok === false || stale ? 'bad' : 'ok'}>
                  <strong>{JOB_LABEL[j.job] ?? j.job}</strong>
                  <span>
                    {ago(j.startedAt)}
                    {j.detail ? ` · ${j.detail.replace(/^[0-9a-f]{8}: /, '')}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="card">
        <h2>Your activity</h2>
        <p className="sub">
          Every change made on this account, as the server recorded it. Reads are not listed.
        </p>
        {activity.length === 0 && <p>Nothing recorded yet.</p>}
        {activity.length > 0 && (
          <table className="rows">
            <tbody>
              {activity.map((a, i) => (
                <tr key={`${a.request_id ?? 'x'}-${i}`}>
                  <td className="dim">{new Date(a.at).toLocaleString('en-ZA', { hour12: false })}</td>
                  <td>{a.entity === 'http' ? (a.path ?? '') : a.action}</td>
                  <td className="dim">
                    {a.entity === 'http' ? a.action : a.entity}
                    {a.duration_ms != null ? ` · ${a.duration_ms}ms` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
